import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import { API_ENDPOINTS } from '../../config/api';

interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'transfer' | 'payment';
  amount: number;
  description: string;
  created_at: string;
  status: 'completed' | 'pending' | 'failed';
}

const TransactionHistory: React.FC = () => {
  const colors = getThemeColors('dark');

  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No auth token found');
        setTransactions([]);
        return;
      }

      const response = await fetch(API_ENDPOINTS.WALLET_TRANSACTIONS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setTransactions(data.data || []);
      } else {
        console.error('Failed to load transactions:', data.error);
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { id: 'all', name: 'All', icon: '📋' },
    { id: 'deposit', name: 'Deposits', icon: '⬇️' },
    { id: 'withdrawal', name: 'Withdrawals', icon: '⬆️' },
    { id: 'transfer', name: 'Transfers', icon: '↔️' },
  ];

  const filteredTransactions = transactions.filter((transaction: Transaction) => {
    const matchesFilter = selectedFilter === 'all' || transaction.type === selectedFilter;
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getTransactionIcon = (type: string): string => {
    switch (type) {
      case 'deposit': return '⬇️';
      case 'withdrawal': return '⬆️';
      case 'transfer': return '↔️';
      case 'payment': return '💳';
      default: return '💰';
    }
  };

  const getTransactionColor = (type: string): string => {
    switch (type) {
      case 'deposit': return colors.success;
      case 'withdrawal': return colors.error;
      case 'transfer': return colors.info;
      case 'payment': return colors.warning;
      default: return colors.textSecondary;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed': return colors.success;
      case 'pending': return colors.warning;
      case 'failed': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    filters: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    },
    filterButtons: {
      display: 'flex',
      gap: spacing.sm,
      marginBottom: spacing.md,
      flexWrap: 'wrap',
    },
    filterButton: {
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.full,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      transition: 'all 0.2s ease',
    },
    activeFilter: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
      color: colors.primary,
    },
    searchInput: {
      width: '100%',
      padding: spacing.md,
      borderRadius: borderRadius.lg,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
    },
    transactionsList: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      ...getShadowStyle('md'),
      overflow: 'hidden',
    },
    transactionItem: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.lg,
      borderBottom: `1px solid ${colors.border}`,
      transition: 'background-color 0.2s ease',
    },
    transactionIcon: {
      width: 40,
      height: 40,
      borderRadius: borderRadius.full,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.md,
      fontSize: 18,
    },
    transactionInfo: {
      flex: 1,
    },
    transactionType: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.xs / 2,
    },
    transactionDescription: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs / 2,
    },
    transactionDate: {
      fontSize: typography.fontSize.xs,
      color: colors.textTertiary,
    },
    transactionAmount: {
      textAlign: 'right',
    },
    amount: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      marginBottom: spacing.xs / 2,
    },
    status: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
      padding: `${spacing.xs / 2} ${spacing.sm}`,
      borderRadius: borderRadius.full,
      textTransform: 'uppercase',
    },
    emptyState: {
      textAlign: 'center',
      padding: spacing.xl,
      color: colors.textSecondary,
    },
    emptyIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Transactions"
        subtitle="View and manage your transaction records"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          <div style={styles.filters}>
        <div style={styles.filterButtons}>
          {filters.map((filter) => (
            <button
              key={filter.id}
              style={{
                ...styles.filterButton,
                ...(selectedFilter === filter.id ? styles.activeFilter : {}),
              }}
              onClick={() => setSelectedFilter(filter.id)}
            >
              <span style={{ marginRight: spacing.xs }}>{filter.icon}</span>
              {filter.name}
            </button>
          ))}
        </div>

        <input
          type="text"
          style={styles.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search transactions..."
        />
      </div>

      <div style={styles.transactionsList}>
        {filteredTransactions.length > 0 ? (
          filteredTransactions.map((transaction: Transaction) => (
            <div
              key={transaction.id}
              style={styles.transactionItem}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.backgroundSecondary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div
                style={{
                  ...styles.transactionIcon,
                  backgroundColor: getTransactionColor(transaction.type) + '20',
                }}
              >
                <span style={{ color: getTransactionColor(transaction.type) }}>
                  {getTransactionIcon(transaction.type)}
                </span>
              </div>

              <div style={styles.transactionInfo}>
                <div style={styles.transactionType}>{transaction.description}</div>
                <div style={styles.transactionDescription}>
                  {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                </div>
                <div style={styles.transactionDate}>
                  {new Date(transaction.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>

              <div style={styles.transactionAmount}>
                <div
                  style={{
                    ...styles.amount,
                    color: transaction.amount > 0 ? colors.success : colors.error,
                  }}
                >
                  {transaction.amount > 0 ? '+' : ''}{formatCurrency(Math.abs(transaction.amount))}
                </div>
                <div
                  style={{
                    ...styles.status,
                    backgroundColor: getStatusColor(transaction.status) + '20',
                    color: getStatusColor(transaction.status),
                  }}
                >
                  {transaction.status}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📋</div>
            <div>No transactions found matching your criteria.</div>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;